import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { confirmAction } from '../utils/confirm';
import {
    fetchQrPoints, createQrPoint, updateQrPoint, newQrPointToken, deleteQrPoint, QrPoint,
} from '../services/qrApi';
import { fetchZones } from '../services/zonesApi';
import { fetchInspectionTemplates, InspectionTemplate } from '../services/inspectionsApi';
import { ui } from '../styles/shared';
import PageHeader from './shared/PageHeader';

/**
 * QR-punkter: lappar som sätts upp på stugor/platser. Skanningen öppnar /qr/<token>
 * där en kontakt (identifierad med sms-kod) gör den kopplade kontrollen.
 *
 * Utskriften är en ren etikettvy: markera punkter → Skriv ut → webbläsarens utskrift.
 */

const qrUrl = (token: string) => `${window.location.origin}/qr/${token}`;

const blankForm = () => ({ name: '', zone_id: 0, template_id: 0 });

/** QR-koden som bild (data-URL). Ritas om när länken ändras. */
const QrImage: React.FC<{ url: string; size: number }> = ({ url, size }) => {
    const [src, setSrc] = useState<string | null>(null);
    useEffect(() => {
        let alive = true;
        QRCode.toDataURL(url, { width: size * 2, margin: 1 })
            .then(d => { if (alive) setSrc(d); })
            .catch(() => { if (alive) setSrc(null); });
        return () => { alive = false; };
    }, [url, size]);
    if (!src) return <div style={{ width: size, height: size, background: '#eee' }} />;
    return <img src={src} alt="QR-kod" width={size} height={size} style={{ display: 'block' }} />;
};

const QrPoints: React.FC = () => {
    const [points, setPoints] = useState<QrPoint[]>([]);
    const [zones, setZones] = useState<any[]>([]);
    const [templates, setTemplates] = useState<InspectionTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState(blankForm());
    const [saving, setSaving] = useState(false);

    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [printing, setPrinting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [p, z, t] = await Promise.all([
                fetchQrPoints(), fetchZones(), fetchInspectionTemplates(),
            ]);
            setPoints(p); setZones(z.filter((x: any) => x.kind !== 'area')); setTemplates(t);
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta QR-punkterna');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startCreate = () => { setForm(blankForm()); setEditId(null); setShowForm(true); };
    const startEdit = (p: QrPoint) => {
        setForm({ name: p.name, zone_id: p.zone_id ?? 0, template_id: p.template_id ?? 0 });
        setEditId(p.id); setShowForm(true);
    };

    const save = async () => {
        if (!form.name.trim()) { setError('Skriv ett namn (det som ska stå på lappen)'); return; }
        setSaving(true);
        try {
            const body = {
                name: form.name.trim(),
                zone_id: form.zone_id || null,
                template_id: form.template_id || null,
            };
            if (editId) {
                const updated = await updateQrPoint(editId, body);
                setPoints(ps => ps.map(p => (p.id === editId ? updated : p)));
            } else {
                const created = await createQrPoint(body);
                setPoints(ps => [...ps, created].sort((a, b) => a.name.localeCompare(b.name, 'sv')));
            }
            setShowForm(false); setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte spara');
        } finally { setSaving(false); }
    };

    const toggleActive = async (p: QrPoint) => {
        try {
            const updated = await updateQrPoint(p.id, { active: !p.active } as any);
            setPoints(ps => ps.map(x => (x.id === p.id ? updated : x)));
        } catch (e: any) { setError(e.message || 'Kunde inte spara'); }
    };

    const replaceToken = async (p: QrPoint) => {
        if (!await confirmAction(`Byta QR-kod för "${p.name}"? Den uppsatta lappen slutar gälla och en ny behöver skrivas ut.`)) return;
        try {
            const updated = await newQrPointToken(p.id);
            setPoints(ps => ps.map(x => (x.id === p.id ? updated : x)));
        } catch (e: any) { setError(e.message || 'Kunde inte byta QR-kod'); }
    };

    const remove = async (p: QrPoint) => {
        if (!await confirmAction(`Ta bort "${p.name}"? Lappen slutar gälla.`)) return;
        try {
            await deleteQrPoint(p.id);
            setPoints(ps => ps.filter(x => x.id !== p.id));
            setSelected(s => { const n = new Set(s); n.delete(p.id); return n; });
        } catch (e: any) { setError(e.message || 'Kunde inte ta bort'); }
    };

    const copyLink = async (p: QrPoint) => {
        try { await navigator.clipboard.writeText(qrUrl(p.token)); } catch { /* ignore */ }
    };

    const toggleSelected = (id: number) =>
        setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const printPoints = useMemo(
        () => points.filter(p => p.active && (selected.size === 0 || selected.has(p.id))),
        [points, selected]
    );

    const print = () => {
        setPrinting(true);
        // Låt etikettvyn hinna ritas (QR-bilderna är redan genererade i tabellen).
        setTimeout(() => { window.print(); setPrinting(false); }, 300);
    };

    if (loading) return <div>Laddar…</div>;

    return (
        <div>
            {/* Utskrift: bara etiketterna syns. */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .qr-print-sheet, .qr-print-sheet * { visibility: visible; }
                    .qr-print-sheet { position: absolute; left: 0; top: 0; width: 100%; }
                }
            `}</style>

            <div className="no-print">
                <PageHeader
                    icon="🔗"
                    title="QR-punkter"
                    description="Lappar som sätts upp på stugor och platser. Den som utför kontroller skannar lappen och gör den kopplade kontrollen — första gången identifierar man sig med en sms-kod."
                    actions={
                        <>
                            <button style={ui.btnSecondary} onClick={print} disabled={!printPoints.length}>
                                🖨️ Skriv ut {selected.size > 0 ? `(${printPoints.length} valda)` : 'alla'}
                            </button>
                            <button style={ui.btnPrimary} onClick={startCreate}>+ Ny QR-punkt</button>
                        </>
                    }
                />

                {error && <div style={ui.errorBox}>{error}<button style={ui.btnLightNeutral} onClick={() => setError(null)}>Stäng</button></div>}

                {showForm && (
                    <div style={ui.card}>
                        <h3 style={{ marginTop: 0 }}>{editId ? 'Ändra QR-punkt' : 'Ny QR-punkt'}</h3>
                        <label style={ui.label}>Namn (det som står på lappen)</label>
                        <input style={ui.input} value={form.name} placeholder="t.ex. Stuga 4"
                               onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
                        <label style={ui.label}>Plats</label>
                        <select style={ui.input} value={form.zone_id}
                                onChange={e => setForm(f => ({ ...f, zone_id: Number(e.target.value) }))}>
                            <option value={0}>Ingen (namnet används som plats)</option>
                            {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                        </select>
                        <label style={ui.label}>Kontroll som görs från lappen</label>
                        <select style={ui.input} value={form.template_id}
                                onChange={e => setForm(f => ({ ...f, template_id: Number(e.target.value) }))}>
                            <option value={0}>Ingen vald ännu</option>
                            {templates.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button style={ui.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Sparar…' : 'Spara'}</button>
                            <button style={ui.btnSecondary} onClick={() => setShowForm(false)}>Avbryt</button>
                        </div>
                    </div>
                )}

                <div style={{ ...ui.card, overflowX: 'auto' }}>
                    {points.length === 0 ? (
                        <p style={{ color: '#666', margin: 0 }}>Inga QR-punkter ännu. Skapa en och skriv ut lappen.</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={ui.th}></th>
                                    <th style={ui.th}>QR</th>
                                    <th style={ui.th}>Namn</th>
                                    <th style={ui.th}>Plats</th>
                                    <th style={ui.th}>Kontroll</th>
                                    <th style={ui.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {points.map(p => (
                                    <tr key={p.id} style={{ opacity: p.active ? 1 : 0.5 }}>
                                        <td style={ui.td}>
                                            <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelected(p.id)}
                                                   title="Välj för utskrift" />
                                        </td>
                                        <td style={ui.td}><QrImage url={qrUrl(p.token)} size={56} /></td>
                                        <td style={ui.td}><strong>{p.name}</strong>{!p.active && <span style={{ color: '#999' }}> (av)</span>}</td>
                                        <td style={ui.td}>{p.zone_name ?? '—'}</td>
                                        <td style={ui.td}>{p.template_name ?? '—'}</td>
                                        <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                <button style={ui.btnLight} onClick={() => startEdit(p)}>Ändra</button>
                                                <button style={ui.btnLightNeutral} onClick={() => copyLink(p)} title={qrUrl(p.token)}>Kopiera länk</button>
                                                <button style={ui.btnLightNeutral} onClick={() => toggleActive(p)}>{p.active ? 'Stäng av' : 'Slå på'}</button>
                                                <button style={ui.btnLightNeutral} onClick={() => replaceToken(p)} title="Lappen som sitter uppe slutar gälla">Byt QR-kod</button>
                                                <button style={ui.btnLightDanger} onClick={() => remove(p)}>Ta bort</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Etikettvyn: ritas alltid (gömd på skärmen) så att utskriften har QR-bilderna klara. */}
            <div className="qr-print-sheet" style={{ display: printing ? 'block' : 'none' }} aria-hidden>
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, padding: 24,
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}>
                    {printPoints.map(p => (
                        <div key={p.id} style={{
                            border: '1px dashed #999', borderRadius: 8, padding: 20, textAlign: 'center',
                            breakInside: 'avoid', pageBreakInside: 'avoid',
                        }}>
                            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>{p.name}</div>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <QrImage url={qrUrl(p.token)} size={180} />
                            </div>
                            <div style={{ fontSize: 14, marginTop: 12, color: '#333' }}>
                                Skanna för att göra kontrollen
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default QrPoints;
